import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    Badge,
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Grid,
    HStack,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Slider,
    SliderFilledTrack,
    SliderThumb,
    SliderTrack,
    Spinner,
    Stack,
    Text,
    keyframes,
    useColorModeValue,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import {FaSearch} from "react-icons/fa";
import {
    MdAdd,
    MdLibraryMusic,
    MdMusicNote,
    MdPause,
    MdPlayArrow,
    MdRepeat,
    MdSchedule,
    MdShuffle,
    MdSkipNext,
    MdSkipPrevious,
    MdUploadFile,
    MdVolumeOff,
    MdVolumeUp,
} from "react-icons/md";
import {motion} from "framer-motion";
import {CATEGORIES, formatTime, mapApiTrack} from "./catalog";
import {fileUpload} from "../../../Services/FileService";
import {createMusic, listMusic} from "../../../Services/MusicLibraryService";
import AutoPlaySettingsModal from "./AutoPlaySettingsModal";
import {isWithinSchedule, readAutoPlaySettings} from "../../../utils/MusicScheduleUtils";

const MotionBox = motion(Box);

const bars = keyframes`
    0%, 100% { transform: scaleY(0.35); }
    50%      { transform: scaleY(1); }
`;

/* ---------------- animated equalizer badge on the playing tile ------------- */
function Equalizer() {
    return (
        <HStack spacing="3px" h="18px" align="flex-end">
            {[0, 1, 2, 3, 4].map((i) => (
                <Box
                    key={i}
                    w="3px"
                    h="100%"
                    borderRadius="full"
                    bg="white"
                    transformOrigin="bottom"
                    animation={`${bars} 0.9s ease-in-out ${i * 0.1}s infinite`}
                />
            ))}
        </HStack>
    );
}

/* ------------------------------ album tile -------------------------------- */
function AlbumTile({track, isCurrent, isPlaying, onPlay}) {
    return (
        <MotionBox
            whileHover={{y: -6}}
            transition={{duration: 0.2}}
            cursor="pointer"
            onClick={() => onPlay(track)}
            role="group"
        >
            <Box
                position="relative"
                borderRadius="18px"
                overflow="hidden"
                boxShadow={isCurrent ? "0 12px 30px rgba(102,126,234,0.45)" : "md"}
                _groupHover={{boxShadow: "xl"}}
            >
                <Box
                    h={{base: "150px", md: "180px"}}
                    bgGradient={track.cover}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    <Icon as={MdMusicNote} boxSize="54px" color="whiteAlpha.700"/>
                </Box>

                {/* play / now-playing overlay */}
                <Flex
                    position="absolute"
                    inset="0"
                    align="center"
                    justify="center"
                    bg="blackAlpha.400"
                    opacity={isCurrent ? 1 : 0}
                    _groupHover={{opacity: 1}}
                    transition="opacity 0.2s"
                >
                    {isCurrent && isPlaying ? (
                        <Flex
                            w="54px"
                            h="54px"
                            borderRadius="full"
                            bg="whiteAlpha.300"
                            backdropFilter="blur(4px)"
                            align="center"
                            justify="center"
                        >
                            <Equalizer/>
                        </Flex>
                    ) : (
                        <Flex
                            w="54px"
                            h="54px"
                            borderRadius="full"
                            bg="white"
                            align="center"
                            justify="center"
                            boxShadow="lg"
                        >
                            <Icon as={MdPlayArrow} boxSize="30px" color="gray.800"/>
                        </Flex>
                    )}
                </Flex>

                <Badge
                    position="absolute"
                    top="10px"
                    left="10px"
                    colorScheme="blackAlpha"
                    bg="blackAlpha.600"
                    color="white"
                    borderRadius="full"
                    textTransform="capitalize"
                    px={2}
                >
                    {track.category}
                </Badge>
            </Box>

            <Box mt={3} px={1}>
                <Text fontWeight="bold" noOfLines={1} color={isCurrent ? "teal.500" : undefined}>
                    {track.title}
                </Text>
                <Text fontSize="sm" color="gray.500" noOfLines={1}>
                    {track.artist}
                </Text>
            </Box>
        </MotionBox>
    );
}

/* ---------------------------- add-music modal ----------------------------- */
function AddMusicModal({isOpen, onClose, onSaved}) {
    const toast = useToast();
    const [form, setForm] = useState({name: "", album: "", artist: ""});
    const [s3Url, setS3Url] = useState("");
    const [fileName, setFileName] = useState("");
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    const reset = useCallback(() => {
        setForm({name: "", album: "", artist: ""});
        setS3Url("");
        setFileName("");
        setUploading(false);
        setSaving(false);
    }, []);

    useEffect(() => {
        if (!isOpen) reset();
    }, [isOpen, reset]);

    const handleFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        setFileName(file.name);
        if (!form.name) {
            setForm((prev) => ({...prev, name: file.name.replace(/\.[^.]+$/, "")}));
        }
        setUploading(true);
        try {
            const result = await fileUpload(file);
            const url = typeof result === "string" ? result : result?.url || result?.s3Url || result?.fileUrl || result?.key || "";
            if (!url) throw new Error("Empty upload response");
            setS3Url(url);
            toast({title: "File uploaded", status: "success", duration: 2000, isClosable: true});
        } catch (e) {
            toast({title: "Upload failed", description: "Could not upload the audio file.", status: "error", duration: 4000, isClosable: true});
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async () => {
        if (!form.name.trim()) {
            toast({title: "Enter a track name", status: "warning", duration: 2500, isClosable: true});
            return;
        }
        if (!s3Url) {
            toast({title: "Upload an audio file first", status: "warning", duration: 2500, isClosable: true});
            return;
        }
        setSaving(true);
        try {
            const response = await createMusic({...form, s3Url});
            if (response.status === 201 || response.status === 200) {
                toast({title: "Added to library", status: "success", duration: 3000, isClosable: true});
                onSaved();
                onClose();
            } else {
                throw new Error("Unexpected status");
            }
        } catch (e) {
            toast({title: "Could not save", description: "Saving the track failed.", status: "error", duration: 4000, isClosable: true});
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>Add Music</ModalHeader>
                <ModalCloseButton/>
                <ModalBody>
                    <Stack spacing={4}>
                        <FormControl isRequired>
                            <FormLabel>Track name</FormLabel>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm((prev) => ({...prev, name: e.target.value}))}
                                placeholder="e.g. Morning Bell Tune"
                            />
                        </FormControl>
                        <HStack spacing={4} align="flex-start">
                            <FormControl>
                                <FormLabel>Artist</FormLabel>
                                <Input
                                    value={form.artist}
                                    onChange={(e) => setForm((prev) => ({...prev, artist: e.target.value}))}
                                    placeholder="e.g. Studio Nepal"
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Album</FormLabel>
                                <Input
                                    value={form.album}
                                    onChange={(e) => setForm((prev) => ({...prev, album: e.target.value}))}
                                    placeholder="e.g. School Bells Vol. 1"
                                />
                            </FormControl>
                        </HStack>
                        <FormControl isRequired>
                            <FormLabel>Audio file</FormLabel>
                            <Button
                                as="label"
                                htmlFor="studio-file-input"
                                leftIcon={<MdUploadFile/>}
                                variant="outline"
                                colorScheme="purple"
                                cursor="pointer"
                                isLoading={uploading}
                                loadingText="Uploading"
                                w="full"
                            >
                                {fileName ? "Choose a different file" : "Select audio file"}
                                <input id="studio-file-input" type="file" accept="audio/*" hidden onChange={handleFile}/>
                            </Button>
                            {fileName && (
                                <HStack mt={2} color={s3Url ? "green.600" : "gray.500"} fontSize="sm">
                                    <Icon as={MdMusicNote}/>
                                    <Text noOfLines={1}>{fileName}</Text>
                                    {s3Url && <Badge colorScheme="green">uploaded</Badge>}
                                </HStack>
                            )}
                        </FormControl>
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                    <Button colorScheme="purple" onClick={handleSave} isLoading={saving} isDisabled={!s3Url || !form.name.trim()}>
                        Save
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

/* -------------------------------- page ------------------------------------ */
function MusicStudio() {
    const toast = useToast();
    const {isOpen, onOpen, onClose} = useDisclosure();
    const settingsModal = useDisclosure();
    const [catalog, setCatalog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [autoPlaySettings, setAutoPlaySettings] = useState(readAutoPlaySettings);
    const cardBg = useColorModeValue("white", "gray.800");
    const pageBg = useColorModeValue("gray.50", "gray.900");
    const barBg = useColorModeValue("white", "gray.800");
    const barBorder = useColorModeValue("gray.200", "gray.700");

    const [category, setCategory] = useState("all");
    const [searchTerm, setSearchTerm] = useState("");
    const [currentId, setCurrentId] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(0.8);
    const [muted, setMuted] = useState(false);
    const [shuffle, setShuffle] = useState(false);
    const [repeat, setRepeat] = useState(false);

    const audioRef = useRef(null);
    // Tracks an in-progress scheduled auto-play session so ticks don't restart
    // it every 15s and onEnded can advance through the selected tracks in order.
    const autoSessionRef = useRef({active: false, ids: [], index: 0});

    const fetchTracks = useCallback(async () => {
        setLoading(true);
        try {
            const records = await listMusic();
            setCatalog(records.map((record, index) => mapApiTrack(record, index)));
        } catch (e) {
            toast({title: "Could not load music", description: "Failed to fetch the music library.", status: "error", duration: 4000, isClosable: true});
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        fetchTracks();
    }, [fetchTracks]);

    // Tracks visible after category + search filtering. This is also the active
    // playlist that prev/next walk through.
    const playlist = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return catalog.filter((t) => {
            const matchesCat = category === "all" || t.category === category;
            const matchesTerm =
                !term ||
                t.title.toLowerCase().includes(term) ||
                t.artist.toLowerCase().includes(term) ||
                t.album.toLowerCase().includes(term);
            return matchesCat && matchesTerm;
        });
    }, [catalog, category, searchTerm]);

    const currentTrack = useMemo(
        () => catalog.find((t) => t.id === currentId) || null,
        [catalog, currentId]
    );

    const counts = useMemo(() => {
        const base = {all: catalog.length, music: 0, podcast: 0, bells: 0};
        catalog.forEach((t) => {
            base[t.category] = (base[t.category] || 0) + 1;
        });
        return base;
    }, [catalog]);

    const playTrack = useCallback((track) => {
        const audio = audioRef.current;
        if (!audio) return;

        // Toggle when clicking the track that's already loaded.
        if (track.id === currentId) {
            if (audio.paused) {
                audio.play().then(() => setIsPlaying(true)).catch(() => {});
            } else {
                audio.pause();
                setIsPlaying(false);
            }
            return;
        }

        audio.src = track.src;
        audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
        setCurrentId(track.id);
        setProgress(0);
        setCurrentTime(0);
    }, [currentId]);

    // Always (re)load and play — unlike playTrack it never toggles, so a single
    // scheduled track can loop. Used only by the auto-play scheduler.
    const forcePlay = useCallback((track) => {
        const audio = audioRef.current;
        if (!audio || !track) return;
        audio.src = track.src;
        audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
        setCurrentId(track.id);
        setProgress(0);
        setCurrentTime(0);
    }, []);

    const stopPlayback = useCallback(() => {
        const audio = audioRef.current;
        if (audio) {
            audio.pause();
            audio.currentTime = 0;
        }
        setIsPlaying(false);
    }, []);

    // Play the scheduled track at `index` (wrapping), resolving the id back to a
    // catalog track. Skips missing tracks by advancing until one is found.
    const playScheduleIndex = useCallback((index) => {
        const {ids} = autoSessionRef.current;
        if (!ids.length) return;
        for (let i = 0; i < ids.length; i++) {
            const nextIdx = (index + i) % ids.length;
            const track = catalog.find((t) => String(t.id) === String(ids[nextIdx]));
            if (track) {
                autoSessionRef.current.index = nextIdx;
                forcePlay(track);
                return;
            }
        }
    }, [catalog, forcePlay]);

    // In-page auto-play scheduler. While this page is open it checks every 15s
    // whether "now" is inside the saved window on a matching weekday; if so it
    // starts the selected tracks (looping until the window ends). Full-app
    // scheduling would move this into the layout, like BellScheduler.
    useEffect(() => {
        const tick = () => {
            const within = isWithinSchedule(autoPlaySettings);
            const session = autoSessionRef.current;
            if (within && !session.active) {
                session.active = true;
                session.ids = autoPlaySettings.trackIds.map(String);
                session.index = 0;
                playScheduleIndex(0);
            } else if (!within && session.active) {
                session.active = false;
                session.ids = [];
                stopPlayback();
            }
        };
        tick();
        const timer = setInterval(tick, 15000);
        return () => clearInterval(timer);
    }, [autoPlaySettings, playScheduleIndex, stopPlayback]);

    const handleScheduleSaved = useCallback((next) => {
        autoSessionRef.current = {active: false, ids: [], index: 0};
        setAutoPlaySettings(next);
    }, []);

    const step = useCallback((dir) => {
        if (!playlist.length) return;
        if (shuffle) {
            const pool = playlist.filter((t) => t.id !== currentId);
            const next = (pool.length ? pool : playlist)[Math.floor(Math.random() * (pool.length ? pool.length : playlist.length))];
            playTrack(next);
            return;
        }
        const idx = playlist.findIndex((t) => t.id === currentId);
        const base = idx === -1 ? 0 : idx;
        const nextIdx = (base + dir + playlist.length) % playlist.length;
        playTrack(playlist[nextIdx]);
    }, [playlist, shuffle, currentId, playTrack]);

    const togglePlay = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;
        if (!currentTrack) {
            if (playlist.length) playTrack(playlist[0]);
            return;
        }
        if (audio.paused) {
            audio.play().then(() => setIsPlaying(true)).catch(() => {});
        } else {
            audio.pause();
            setIsPlaying(false);
        }
    }, [currentTrack, playlist, playTrack]);

    const handleSeek = (value) => {
        const audio = audioRef.current;
        if (audio && duration) {
            audio.currentTime = (value / 100) * duration;
            setProgress(value);
        }
    };

    // Keep the <audio> element's volume in sync with the UI.
    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = muted ? 0 : volume;
        }
    }, [volume, muted]);

    return (
        <Flex
            direction="column"
            pt={{base: "120px", md: "80px"}}
            px={{base: "4", md: "8"}}
            pb="130px"
            bg={pageBg}
            minH="100vh"
        >
            {/* hero header */}
            <Box
                borderRadius="24px"
                p={{base: 6, md: 8}}
                mb={6}
                bgGradient="linear(135deg, #6a11cb 0%, #2575fc 100%)"
                color="white"
                boxShadow="0 12px 40px rgba(37,117,252,0.35)"
            >
                <Flex justify="space-between" align={{base: "flex-start", md: "center"}} direction={{base: "column", md: "row"}} gap={4}>
                    <HStack spacing={4}>
                        <Flex w="52px" h="52px" borderRadius="16px" bg="whiteAlpha.300" align="center" justify="center">
                            <Icon as={MdLibraryMusic} boxSize="28px"/>
                        </Flex>
                        <Box>
                            <Text fontSize={{base: "xl", md: "2xl"}} fontWeight="extrabold">Music Studio</Text>
                            <Text color="whiteAlpha.800" fontSize="sm">
                                {catalog.length} tracks across music, podcasts &amp; bells
                            </Text>
                        </Box>
                    </HStack>
                    <HStack spacing={3}>
                        <Button
                            leftIcon={<MdSchedule/>}
                            variant="outline"
                            color="white"
                            borderColor="whiteAlpha.700"
                            _hover={{bg: "whiteAlpha.200"}}
                            onClick={settingsModal.onOpen}
                        >
                            Auto-Play
                            {autoPlaySettings.enabled && (
                                <Badge ml={2} colorScheme="green" borderRadius="full">on</Badge>
                            )}
                        </Button>
                        <Button
                            leftIcon={<MdAdd/>}
                            bg="white"
                            color="purple.600"
                            _hover={{bg: "whiteAlpha.900"}}
                            onClick={onOpen}
                        >
                            Add Music
                        </Button>
                    </HStack>
                </Flex>
            </Box>

            {/* category tabs + search */}
            <Flex
                justify="space-between"
                align={{base: "stretch", md: "center"}}
                direction={{base: "column", md: "row"}}
                gap={4}
                mb={6}
            >
                <InputGroup flex="1" maxW={{base: "full", md: "560px"}} bg={cardBg} borderRadius="full" boxShadow="sm">
                    <InputLeftElement pointerEvents="none" children={<FaSearch color="gray"/>}/>
                    <Input
                        borderRadius="full"
                        placeholder="Search tracks, artists, albums…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </InputGroup>

                <HStack spacing={2} flexWrap="wrap" justify={{base: "flex-start", md: "flex-end"}}>
                    {CATEGORIES.map((cat) => {
                        const active = category === cat.key;
                        return (
                            <Button
                                key={cat.key}
                                size="sm"
                                borderRadius="full"
                                variant={active ? "solid" : "outline"}
                                colorScheme="purple"
                                onClick={() => setCategory(cat.key)}
                            >
                                {cat.label}
                                <Badge ml={2} borderRadius="full" colorScheme={active ? "whiteAlpha" : "purple"} bg={active ? "whiteAlpha.400" : undefined}>
                                    {counts[cat.key] || 0}
                                </Badge>
                            </Button>
                        );
                    })}
                </HStack>
            </Flex>

            {/* album grid */}
            {loading ? (
                <Flex direction="column" align="center" justify="center" py="80px" color="gray.400">
                    <Spinner size="lg" color="purple.400" thickness="3px" mb={3}/>
                    <Text fontSize="sm">Loading music…</Text>
                </Flex>
            ) : playlist.length === 0 ? (
                <Flex direction="column" align="center" justify="center" py="80px" color="gray.400">
                    <Icon as={MdLibraryMusic} boxSize="56px" mb={3}/>
                    <Text fontWeight="bold" fontSize="lg">{catalog.length === 0 ? "Library is empty" : "No tracks found"}</Text>
                    <Text fontSize="sm">
                        {catalog.length === 0 ? "Click “Add Music” to upload your first track." : "Try a different category or search term."}
                    </Text>
                </Flex>
            ) : (
                <Grid
                    templateColumns={{base: "1fr 1fr", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)", xl: "repeat(5, 1fr)"}}
                    gap={{base: 4, md: 6}}
                >
                    {playlist.map((track) => (
                        <AlbumTile
                            key={track.id}
                            track={track}
                            isCurrent={track.id === currentId}
                            isPlaying={isPlaying}
                            onPlay={playTrack}
                        />
                    ))}
                </Grid>
            )}

            {/* hidden audio engine */}
            <audio
                ref={audioRef}
                onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
                onTimeUpdate={(e) => {
                    const el = e.currentTarget;
                    setCurrentTime(el.currentTime || 0);
                    setDuration(el.duration || 0);
                    setProgress(el.duration ? (el.currentTime / el.duration) * 100 : 0);
                }}
                onEnded={() => {
                    // During a scheduled session, walk the selected tracks in order.
                    if (autoSessionRef.current.active && autoSessionRef.current.ids.length) {
                        playScheduleIndex(autoSessionRef.current.index + 1);
                    } else if (repeat && audioRef.current) {
                        audioRef.current.currentTime = 0;
                        audioRef.current.play().catch(() => {});
                    } else {
                        step(1);
                    }
                }}
            />

            {/* now-playing bar */}
            {currentTrack && (
                <Flex
                    position="fixed"
                    bottom="0"
                    left={{base: "0", xl: "260px"}}
                    right="0"
                    bg={barBg}
                    borderTop="1px solid"
                    borderColor={barBorder}
                    boxShadow="0 -6px 24px rgba(0,0,0,0.10)"
                    px={{base: 3, md: 6}}
                    py={3}
                    align="center"
                    gap={{base: 3, md: 5}}
                    zIndex={30}
                >
                    {/* track identity */}
                    <HStack spacing={3} minW="0" w={{base: "auto", md: "230px"}} flexShrink={0}>
                        <Box w="48px" h="48px" borderRadius="10px" bgGradient={currentTrack.cover} flexShrink={0}/>
                        <Box minW="0" display={{base: "none", sm: "block"}}>
                            <Text fontWeight="bold" noOfLines={1}>{currentTrack.title}</Text>
                            <Text fontSize="xs" color="gray.500" noOfLines={1}>{currentTrack.artist}</Text>
                        </Box>
                    </HStack>

                    {/* transport + seek */}
                    <Flex direction="column" flex="1" minW="0">
                        <HStack justify="center" spacing={{base: 1, md: 2}} mb={1}>
                            <IconButton
                                aria-label="Shuffle"
                                icon={<MdShuffle/>}
                                size="sm"
                                variant="ghost"
                                colorScheme={shuffle ? "purple" : "gray"}
                                onClick={() => setShuffle((s) => !s)}
                            />
                            <IconButton aria-label="Previous" icon={<MdSkipPrevious/>} size="sm" variant="ghost" onClick={() => step(-1)}/>
                            <IconButton
                                aria-label={isPlaying ? "Pause" : "Play"}
                                icon={isPlaying ? <MdPause/> : <MdPlayArrow/>}
                                colorScheme="purple"
                                isRound
                                size="md"
                                onClick={togglePlay}
                            />
                            <IconButton aria-label="Next" icon={<MdSkipNext/>} size="sm" variant="ghost" onClick={() => step(1)}/>
                            <IconButton
                                aria-label="Repeat"
                                icon={<MdRepeat/>}
                                size="sm"
                                variant="ghost"
                                colorScheme={repeat ? "purple" : "gray"}
                                onClick={() => setRepeat((r) => !r)}
                            />
                        </HStack>
                        <HStack spacing={3}>
                            <Text fontSize="xs" color="gray.500" w="36px" textAlign="right">{formatTime(currentTime)}</Text>
                            <Slider aria-label="Seek" flex="1" value={progress} onChange={handleSeek} focusThumbOnChange={false}>
                                <SliderTrack><SliderFilledTrack bg="purple.400"/></SliderTrack>
                                <SliderThumb boxSize={3}/>
                            </Slider>
                            <Text fontSize="xs" color="gray.500" w="36px">{formatTime(duration)}</Text>
                        </HStack>
                    </Flex>

                    {/* volume */}
                    <HStack spacing={2} w="140px" display={{base: "none", md: "flex"}} flexShrink={0}>
                        <IconButton
                            aria-label="Mute"
                            icon={muted || volume === 0 ? <MdVolumeOff/> : <MdVolumeUp/>}
                            size="sm"
                            variant="ghost"
                            onClick={() => setMuted((m) => !m)}
                        />
                        <Slider
                            aria-label="Volume"
                            value={muted ? 0 : volume * 100}
                            onChange={(v) => {
                                setMuted(false);
                                setVolume(v / 100);
                            }}
                        >
                            <SliderTrack><SliderFilledTrack bg="purple.400"/></SliderTrack>
                            <SliderThumb boxSize={3}/>
                        </Slider>
                    </HStack>
                </Flex>
            )}

            <AddMusicModal isOpen={isOpen} onClose={onClose} onSaved={fetchTracks}/>
            <AutoPlaySettingsModal
                isOpen={settingsModal.isOpen}
                onClose={settingsModal.onClose}
                tracks={catalog}
                onSaved={handleScheduleSaved}
            />
        </Flex>
    );
}

export default MusicStudio;
